/* ==========================================================================
   OfficehaulVault — theme.js
   Vanilla JS, no dependencies. Handles:
   1. AJAX cart (add / change / remove — no page reloads)
   2. Cart drawer open/close + re-render
   3. Quantity steppers
   4. Product gallery thumbnails
   5. Mobile nav + collection filter toggles
   6. Client-side price filter
   7. Scroll-reveal animations
   ========================================================================== */
(function () {
  'use strict';

  var routes = (window.OHV && window.OHV.routes) || {
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_url: '/cart'
  };

  /* ---------- Helpers ---------------------------------------------------- */

  function formatMoney(cents) {
    // Simple money formatter; respects shop format loosely (USD-style fallback)
    var amount = (cents / 100).toFixed(2);
    var format = (window.OHV && window.OHV.moneyFormat) || '${{amount}}';
    return format.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function showToast(message) {
    var toast = qs('.toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { toast.classList.remove('is-visible'); }, 2600);
  }

  /* ---------- Cart state -------------------------------------------------- */

  function fetchCart() {
    return fetch(routes.cart_url + '.js').then(function (r) { return r.json(); });
  }

  function updateCartCount(cart) {
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.setAttribute('data-count', cart.item_count);
    });
  }

  function renderCartDrawer(cart) {
    var itemsEl = qs('[data-cart-items]');
    var subtotalEl = qs('[data-cart-subtotal]');
    var footEl = qs('[data-cart-foot]');
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML =
        '<div class="cart-drawer__empty">' +
        '<p>Your cart is empty.</p>' +
        '<a class="btn btn--primary" href="/collections/all">Shop the collection</a>' +
        '</div>';
      if (footEl) footEl.hidden = true;
      return;
    }

    if (footEl) footEl.hidden = false;

    itemsEl.innerHTML = cart.items.map(function (item) {
      var img = item.image
        ? item.image.replace(/(\.[a-zA-Z]+)(\?.*)?$/, '_160x160$1$2')
        : '';
      return (
        '<div class="cart-item" data-line-key="' + item.key + '">' +
          (img
            ? '<img class="cart-item__img" src="' + img + '" alt="' + (item.title || '').replace(/"/g, '&quot;') + '" loading="lazy" width="72" height="72">'
            : '<div class="cart-item__img"></div>') +
          '<div>' +
            '<p class="cart-item__title">' + item.product_title + '</p>' +
            (item.variant_title && item.variant_title !== 'Default Title'
              ? '<p class="cart-item__variant">' + item.variant_title + '</p>' : '') +
            '<div class="qty" data-line-qty>' +
              '<button type="button" data-qty-minus aria-label="Decrease quantity">&minus;</button>' +
              '<input type="number" value="' + item.quantity + '" min="0" aria-label="Quantity" data-qty-input>' +
              '<button type="button" data-qty-plus aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="cart-item__remove" data-remove-item>Remove</button>' +
          '</div>' +
          '<div class="cart-item__price">' + formatMoney(item.final_line_price) + '</div>' +
        '</div>'
      );
    }).join('');

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
  }

  function refreshCart(openDrawer) {
    return fetchCart().then(function (cart) {
      updateCartCount(cart);
      renderCartDrawer(cart);
      if (openDrawer) document.body.classList.add('cart-open');
      return cart;
    });
  }

  /* ---------- AJAX add to cart ------------------------------------------- */

  function addToCart(variantId, quantity) {
    return fetch(routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: quantity || 1 })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw e; });
      return r.json();
    });
  }

  function changeLine(lineKey, quantity) {
    return fetch(routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  /* ---------- Event delegation -------------------------------------------- */

  document.addEventListener('click', function (e) {
    var t = e.target;

    /* Quick add (product cards) */
    var quickAdd = t.closest('[data-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      quickAdd.disabled = true;
      addToCart(quickAdd.getAttribute('data-quick-add'), 1)
        .then(function () { return refreshCart(true); })
        .then(function () { showToast('Added to cart'); })
        .catch(function (err) { showToast(err && err.description ? err.description : 'Could not add to cart'); })
        .finally(function () { quickAdd.disabled = false; });
      return;
    }

    /* Open / close drawer */
    if (t.closest('[data-cart-open]')) {
      e.preventDefault();
      refreshCart(true);
      return;
    }
    if (t.closest('[data-cart-close]') || t.closest('.cart-drawer__overlay')) {
      document.body.classList.remove('cart-open');
      return;
    }

    /* Quantity steppers (product page + drawer) */
    var minus = t.closest('[data-qty-minus]');
    var plus = t.closest('[data-qty-plus]');
    if (minus || plus) {
      var wrap = (minus || plus).closest('.qty');
      var input = qs('[data-qty-input]', wrap);
      var val = parseInt(input.value, 10) || 0;
      input.value = plus ? val + 1 : Math.max(input.min === '0' ? 0 : 1, val - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    /* Remove line item */
    var remove = t.closest('[data-remove-item]');
    if (remove) {
      var line = remove.closest('[data-line-key]');
      changeLine(line.getAttribute('data-line-key'), 0).then(function () { refreshCart(false); });
      return;
    }

    /* Mobile nav toggle */
    var navToggle = t.closest('[data-nav-toggle]');
    if (navToggle) {
      var nav = qs('[data-site-nav]');
      var open = nav.classList.toggle('is-open');
      navToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    /* Collection filter toggle (mobile) */
    var filterToggle = t.closest('[data-filter-toggle]');
    if (filterToggle) {
      var panel = qs('[data-filter-panel]');
      if (panel) panel.classList.toggle('is-open');
      return;
    }
  });

  /* Drawer line-quantity change */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('.cart-drawer [data-qty-input]');
    if (input) {
      var line = input.closest('[data-line-key]');
      changeLine(line.getAttribute('data-line-key'), parseInt(input.value, 10) || 0)
        .then(function () { refreshCart(false); });
    }
  });

  /* Escape closes drawer */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') document.body.classList.remove('cart-open');
  });

  /* ---------- Product form (main add-to-cart) ----------------------------- */

  qsa('[data-product-form]').forEach(function (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = qs('[type="submit"]', form);
      var id = qs('[name="id"]', form).value;
      var qty = parseInt((qs('[data-qty-input]', form) || {}).value, 10) || 1;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      addToCart(id, qty)
        .then(function () { return refreshCart(true); })
        .then(function () { showToast('Added to cart'); })
        .catch(function (err) { showToast(err && err.description ? err.description : 'Could not add to cart'); })
        .finally(function () { btn.disabled = false; btn.textContent = label; });
    });
  });

  /* ---------- Product gallery --------------------------------------------- */

  qsa('[data-gallery]').forEach(function (gallery) {
    var main = qs('[data-gallery-main] img', gallery);
    qsa('[data-gallery-thumb]', gallery).forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (!main) return;
        main.src = thumb.getAttribute('data-full');
        main.alt = qs('img', thumb) ? qs('img', thumb).alt : main.alt;
        qsa('[data-gallery-thumb]', gallery).forEach(function (x) { x.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  });

  /* ---------- Client-side price filter (collection page) ------------------ */

  function applyPriceFilter() {
    var minEl = qs('[data-price-min]');
    var maxEl = qs('[data-price-max]');
    if (!minEl || !maxEl) return;
    var min = parseFloat(minEl.value) || 0;
    var max = parseFloat(maxEl.value) || Infinity;
    qsa('[data-product-price]').forEach(function (card) {
      var price = parseFloat(card.getAttribute('data-product-price'));
      card.style.display = price >= min && price <= max ? '' : 'none';
    });
  }
  ['data-price-min', 'data-price-max'].forEach(function (attr) {
    var el = qs('[' + attr + ']');
    if (el) el.addEventListener('input', applyPriceFilter);
  });

  /* ---------- Scroll reveal ------------------------------------------------ */

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    qsa('.reveal').forEach(function (el) { io.observe(el); });
  } else {
    qsa('.reveal').forEach(function (el) { el.classList.add('in-view'); });
  }

  /* ---------- Initial cart count ------------------------------------------ */
  refreshCart(false);
})();
